import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import { Record } from 'immutable';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Hooks
import { useAsanaData, useToggle } from 'Shared/utils/hooks';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';

// Actions
import * as MeetingItemActions from 'Shared/actions/MeetingItems.actions';

// Components
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpAlert from 'Wpds/components/alert/WpAlert';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';

// Utils
import { trackOpenedModal } from 'Shared/tracking/tracking';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Styles
import './CopyToAsanaModal.scss';

type Props = {
  meetingItem: Record<MeetingItem>;
  isOpen: boolean;
  toggleModal(): void;
};

const CopyToAsanaModal = ({ meetingItem, isOpen, toggleModal }: Props) => {
  const [project, setProject] = useState(null);
  const [isSaving, toggleIsSaving] = useToggle(false);
  const asanaData = useAsanaData();
  const dispatch = useDispatch();
  const projects = asanaData?.projects;
  const optionsForSelect = projects ? projects.map(({ id, name }) => ({ value: id, label: name })) : [];

  const uuid = MeetingItemHelpers.getUuid(meetingItem);

  useEffect(() => {
    if (isOpen) {
      trackOpenedModal(TrackingConstants.MODAL_TYPES.COPY_TO_ASANA);
    }
  }, [isOpen]);

  const handleSelectChange = value => {
    setProject(value);
    return null;
  };

  const handleCreateTaskClick = useCallback(() => {
    toggleIsSaving();
    const asanaProjectId = project.value;
    dispatch(MeetingItemActions.createAsanaTask(uuid, asanaProjectId)).then(() => {
      toggleModal();
    });
  }, [meetingItem, project, toggleIsSaving, toggleModal]);

  if (!isOpen) return null;

  const baseClassName = 'CopyToAsanaModal';
  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={toggleModal}>
      <WpModalHeader>Copy to Asana</WpModalHeader>
      <WpModalBody>
        <div className={`${baseClassName} py-1`}>
          <p>Choose the Asana project you would like to copy this item to.</p>
          <p className={`${baseClassName}__note`}>
            Note: Any subtopics linked to this item will not be exported to Asana
          </p>
          <div className={`${baseClassName}__select`}>
            <Select
              isSearchable
              options={optionsForSelect}
              placeholder="Choose project"
              value={project}
              onChange={handleSelectChange}
              menuPortalTarget={document.body}
              styles={{ menuPortal: base => ({ ...base, zIndex: '1050' }) }}
            />
          </div>
          {project && (
            <div className={`${baseClassName}__alert`}>
              <WpAlert> Item will be copied to the top of the selected project.</WpAlert>
            </div>
          )}
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={() => toggleModal()}>
            Cancel
          </WpButton>
          <WpButton onClick={handleCreateTaskClick} loading={isSaving} disabled={!project}>
            Create Task
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default CopyToAsanaModal;
